import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/services/APIs';
import { unwrapJsonRoot, normalizeSucursalesList } from '../../utils/sucursalesList';
import './Configuraciones.css';

const readEmpresaIdFromUser = (user) => {
  if (!user || typeof user !== 'object') return '';
  const direct = [
    user.empresaId,
    user.idEmpresa,
    user.empresa_id,
    user.EmpresaID,
    user.empresaID,
    user.companyId,
  ];
  for (const v of direct) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  const nested = user.empresa;
  if (nested && typeof nested === 'object') {
    const nestedVals = [
      nested.empresaId,
      nested.EmpresaID,
      nested.id,
      nested.empresa_id,
    ];
    for (const v of nestedVals) {
      const s = String(v ?? '').trim();
      if (s) return s;
    }
  }
  return '';
};

const SEARCH_PAGE_SIZE = 15;

/** Respuesta paginada `{ data, page, pageSize, total, totalPages }` o array legacy. */
const unwrapSearchPagePayload = (raw) => {
  if (raw == null) {
    return { rows: [], page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 };
  }
  if (Array.isArray(raw)) {
    const total = raw.length;
    return {
      rows: raw,
      page: 1,
      pageSize: SEARCH_PAGE_SIZE,
      total,
      totalPages: total > 0 ? Math.max(1, Math.ceil(total / SEARCH_PAGE_SIZE)) : 0,
    };
  }
  if (typeof raw !== 'object') {
    return { rows: [], page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 };
  }
  const rows = Array.isArray(raw.data) ? raw.data : [];
  const page = Number(raw.page);
  const pageSize = Number(raw.pageSize);
  const total = Number(raw.total);
  const totalPages = Number(raw.totalPages);
  const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  const safeSize =
    Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : SEARCH_PAGE_SIZE;
  const safeTotal = Number.isFinite(total) && total >= 0 ? Math.floor(total) : rows.length;
  let safeTotalPages = Number.isFinite(totalPages) && totalPages >= 0 ? Math.floor(totalPages) : 0;
  if (safeTotalPages < 1 && safeTotal > 0) {
    safeTotalPages = Math.max(1, Math.ceil(safeTotal / safeSize));
  }
  return {
    rows,
    page: safePage,
    pageSize: safeSize,
    total: safeTotal,
    totalPages: safeTotalPages,
  };
};

function ConfigSearchPagination({ page, pageSize, total, totalPages, loading, onPageChange }) {
  if (total < 1) return null;

  const canPrev = page > 1 && !loading;
  const canNext = totalPages > 0 && page < totalPages && !loading;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(totalPages, 1);

  return (
    <nav className="config-search-pagination" aria-label="Paginación de resultados">
      <span className="config-search-pagination-summary">
        {from}–{to} de {total}
      </span>
      <div className="config-search-pagination-actions">
        <button
          type="button"
          className="config-btn-secondary config-search-pagination-btn"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
        >
          Anterior
        </button>
        <span className="config-search-pagination-page">
          Página {page} de {lastPage}
        </span>
        <button
          type="button"
          className="config-btn-secondary config-search-pagination-btn"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
        >
          Siguiente
        </button>
      </div>
    </nav>
  );
}

/**
 * GET users/tipo-cuenta/listado: el backend devuelve un array directo
 * [{ tipoCuenta, clave, nombre }, …]. Solo contemplamos envoltorio simple (data / listado).
 */
const asTipoCuentaArray = (raw) => {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    try {
      return asTipoCuentaArray(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.listado)) return raw.listado;
  }
  return [];
};

const normalizeTiposCuentaOptions = (raw) => {
  const rows = asTipoCuentaArray(raw);
  const out = [];
  for (const row of rows) {
    if (row == null || typeof row !== 'object') continue;
    const value = Number(
      row.tipoCuenta ??
        row.tipoCuentaId ??
        row.TipoCuenta ??
        row.TipoCuentaID ??
        row.id ??
        row.Id
    );
    if (!Number.isFinite(value)) continue;
    const nombre = String(row.nombre ?? row.Nombre ?? '').trim();
    const label = nombre ? `${value} — ${nombre}` : String(value);
    out.push({ value, label });
  }
  return out;
};

const asRolesArray = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.listado)) return raw.listado;
    if (Array.isArray(raw.options)) return raw.options;
    if (Array.isArray(raw.roles)) return raw.roles;
    if (Array.isArray(raw.items)) return raw.items;
  }
  return [];
};

const normalizeRolesList = (raw) => {
  const arr = asRolesArray(raw);
  const out = [];
  for (const row of arr) {
    if (!row || typeof row !== 'object') continue;
    const id = row.RolID ?? row.rolId ?? row.id ?? row.Id ?? row.value;
    const nombre = String(row.nombre ?? row.Nombre ?? row.label ?? '').trim();
    const tipoCuenta = Number(row.tipoCuenta ?? row.TipoCuenta ?? row.tipoCuentaId ?? row.TipoCuentaID);
    const key =
      id != null && String(id).trim() !== ''
        ? String(id)
        : `tmp-${nombre}-${out.length}`;
    out.push({
      key,
      id: id != null && String(id).trim() !== '' ? String(id) : '',
      nombre: nombre || '—',
      tipoCuenta: Number.isFinite(tipoCuenta) ? tipoCuenta : null,
    });
  }
  return out.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));
};

const asUsersArray = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.usuarios)) return raw.usuarios;
    if (Array.isArray(raw.items)) return raw.items;
  }
  return [];
};

const normalizeUsersList = (raw) => {
  const root = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? unwrapJsonRoot(raw) : raw;
  const arr = asUsersArray(root);
  const out = [];
  for (const row of arr) {
    if (!row || typeof row !== 'object') continue;
    const id =
      row.PersonaID ??
      row.personaId ??
      row.UsuarioID ??
      row.usuarioId ??
      row.id ??
      row.Id;
    const nombreUsuario = String(row.nombreUsuario ?? row.NombreUsuario ?? row.username ?? '').trim();
    const nombres = String(row.nombres ?? row.Nombres ?? '').trim();
    const ap = String(row.apellidoPaterno ?? row.ApellidoPaterno ?? '').trim();
    const am = String(row.apellidoMaterno ?? row.ApellidoMaterno ?? '').trim();
    const nombreCompleto = [nombres, ap, am].filter(Boolean).join(' ') || '—';
    const rolObj = row.Rol && typeof row.Rol === 'object' ? row.Rol : row.rol;
    const rolNombre = rolObj ? String(rolObj.Nombre ?? rolObj.nombre ?? '').trim() : '';
    const rid = row.rolId ?? row.RolID ?? row.rol_id ?? rolObj?.RolID ?? rolObj?.rolId;
    const rolDisplay =
      rolNombre ||
      (rid !== undefined && rid !== null && String(rid).trim() !== '' ? String(rid) : '—');
    const activoRaw = row.activo ?? row.Activo ?? row.activa ?? row.Activa;
    const sucursalId = row.sucursalId ?? row.SucursalID ?? row.sucursal_id;
    const sucursalesAsignadasRaw = row.sucursalesAsignadas ?? row.SucursalesAsignadas;
    const sucursalIdsFromApi = row.sucursalIds ?? row.SucursalIDs ?? row.sucursalesIds;
    const fromAsignadas = Array.isArray(sucursalesAsignadasRaw)
      ? sucursalesAsignadasRaw
          .map((s) =>
            s && typeof s === 'object'
              ? (s.sucursalId ?? s.SucursalID ?? s.id ?? s.Id)
              : s
          )
          .map((v) => String(v ?? '').trim())
          .filter(Boolean)
      : [];
    const fromIdsArray = Array.isArray(sucursalIdsFromApi)
      ? sucursalIdsFromApi.map((v) => String(v ?? '').trim()).filter(Boolean)
      : [];
    const sucursalIds =
      fromAsignadas.length > 0
        ? fromAsignadas
        : fromIdsArray.length > 0
          ? fromIdsArray
          : [];
    const activoLabel =
      activoRaw === false ||
      activoRaw === 0 ||
      activoRaw === '0' ||
      String(activoRaw).toLowerCase() === 'false'
        ? 'No'
        : 'Sí';
    const key =
      id != null && String(id).trim() !== ''
        ? String(id)
        : `tmp-${nombreUsuario}-${out.length}`;
    out.push({
      key,
      id: id != null && String(id).trim() !== '' ? String(id) : '',
      nombreUsuario: nombreUsuario || '—',
      nombreCompleto,
      rolDisplay,
      activoLabel,
      sucursalIds:
        sucursalIds.length > 0
          ? Array.from(new Set(sucursalIds))
          : (sucursalId != null && String(sucursalId).trim() !== '' ? [String(sucursalId)] : []),
    });
  }
  return out;
};

const pickEmpresaFromEmpresaCuenta = (root) => {
  const emp = root?.empresa ?? root?.Empresa;
  if (!emp || typeof emp !== 'object') return { empresaId: '', label: '' };
  const id = String(emp.EmpresaID ?? emp.empresaId ?? emp.id ?? '').trim();
  const labelParts = [
    emp.NombreCorto ?? emp.nombreCorto,
    emp.Nombre ?? emp.nombre,
    emp.RazonSocial ?? emp.razonSocial,
    emp.RFC ?? emp.rfc,
  ].filter(Boolean);
  const label = labelParts.join(' · ') || id;
  return { empresaId: id, label };
};

const normalizePermText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const isVisiblePermisoLabel = (label) => {
  const n = normalizePermText(label);
  return n !== 'lectura' && n !== 'read' && n !== 'insert' && n !== 'insertar';
};

const isAccessPermisoLabel = (label) => normalizePermText(label) === 'acceso';

const dedupePermisosByKey = (items) => {
  const map = new Map();
  for (const item of items || []) {
    const key = String(item?.key || '').trim();
    if (!key || map.has(key)) continue;
    map.set(key, item);
  }
  return Array.from(map.values());
};

const dedupePermisosByLabel = (items) => {
  const map = new Map();
  for (const item of items || []) {
    const labelKey = normalizePermText(item?.label);
    if (!labelKey || map.has(labelKey)) continue;
    map.set(labelKey, item);
  }
  return Array.from(map.values());
};

const getActionClavesByModulo = (permisosModel, moduloTitulos = []) => {
  const rows = Array.isArray(permisosModel) ? permisosModel : [];
  const target = new Set((Array.isArray(moduloTitulos) ? moduloTitulos : []).map(normalizePermText));
  const out = new Set();
  const permisoNombreToAccion = {
    ACCESO: 'READ',
    LECTURA: 'READ',
    READ: 'READ',
    AGREGAR: 'CREATE',
    CREAR: 'CREATE',
    CREATE: 'CREATE',
    ELIMINAR: 'DELETE',
    BORRAR: 'DELETE',
    DELETE: 'DELETE',
    ACTUALIZAR: 'UPDATE',
    EDITAR: 'UPDATE',
    UPDATE: 'UPDATE',
  };

  const accionesListForRow = (row) => {
    if (Array.isArray(row?.AccionElemento)) return row.AccionElemento;
    if (Array.isArray(row?.hijos)) return row.hijos;
    return [];
  };

  const navChildNodes = (row) => {
    if (!row || typeof row !== 'object') return [];
    if (Array.isArray(row.hijos)) return row.hijos;
    if (Array.isArray(row.Hijos)) return row.Hijos;
    return [];
  };

  const visit = (row) => {
    if (!row || typeof row !== 'object') return;
    const moduloTitulo = normalizePermText(row?.moduloTitulo ?? row?.Nombre ?? row?.nombre);
    if (moduloTitulo && target.has(moduloTitulo)) {
      for (const accion of accionesListForRow(row)) {
        const clave = String(
          accion?.accionClave ??
            accion?.AccionClave ??
            accion?.Accion?.Clave ??
            accion?.Accion?.Nombre ??
            ''
        )
          .trim()
          .toUpperCase();
        if (clave) out.add(clave);
      }
      const permisos = Array.isArray(row?.Permiso) ? row.Permiso : [];
      for (const permiso of permisos) {
        const nombreRaw = String(permiso?.nombre ?? permiso?.Nombre ?? '').trim().toUpperCase();
        const mapped = permisoNombreToAccion[nombreRaw];
        if (mapped) out.add(mapped);
      }
    }
    for (const child of navChildNodes(row)) {
      visit(child);
    }
  };

  for (const row of rows) visit(row);
  return out;
};

/** Alta usuario admin y alta rol: el backend espera `tipoCuenta` fijo. */
const ALTA_TIPO_CUENTA = 3;

const initialUsuarioForm = () => ({
  nombreUsuario: '',
  password: '',
  passwordConfirm: '',
  nombres: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  sucursalIds: [],
  rolId: '',
  esCuentaSistema: false,
  activo: true,
});

const buildUsuarioPayload = (form) => {
  const payload = {
    nombreUsuario: form.nombreUsuario.trim(),
    password: form.password,
    nombres: form.nombres.trim(),
    activo: Boolean(form.activo),
    esCuentaSistema: Boolean(form.esCuentaSistema),
    es_type_system: Boolean(form.esCuentaSistema),
    tipoCuenta: ALTA_TIPO_CUENTA,
  };

  const ap = form.apellidoPaterno.trim();
  if (ap) payload.apellidoPaterno = ap;

  const am = form.apellidoMaterno.trim();
  if (am) payload.apellidoMaterno = am;

  const sucursalIds = Array.from(
    new Set((Array.isArray(form.sucursalIds) ? form.sucursalIds : []).map((v) => String(v || '').trim()).filter(Boolean))
  );
  // En update siempre enviamos la relación de sucursales para que altas/bajas se reflejen.
  payload.sucursalIds = sucursalIds;
  payload.sucursalId = sucursalIds.length === 1 ? sucursalIds[0] : null;

  const rolId = Number(form.rolId);
  if (String(form.rolId).trim() !== '' && Number.isFinite(rolId)) {
    payload.rolId = rolId;
  }

  return payload;
};

/** Mismo cuerpo que alta; en update la contraseña solo se envía si el usuario escribió una nueva. */
const buildUsuarioUpdatePayload = (form) => {
  const payload = {
    nombreUsuario: form.nombreUsuario.trim(),
    nombres: form.nombres.trim(),
    activo: Boolean(form.activo),
    esCuentaSistema: Boolean(form.esCuentaSistema),
    es_type_system: Boolean(form.esCuentaSistema),
    tipoCuenta: ALTA_TIPO_CUENTA,
  };
  const pwd = String(form.password || '').trim();
  if (pwd) payload.password = pwd;

  const ap = form.apellidoPaterno.trim();
  if (ap) payload.apellidoPaterno = ap;

  const am = form.apellidoMaterno.trim();
  if (am) payload.apellidoMaterno = am;

  const sucursalIds = Array.from(
    new Set((Array.isArray(form.sucursalIds) ? form.sucursalIds : []).map((v) => String(v || '').trim()).filter(Boolean))
  );
  payload.sucursalIds = sucursalIds;
  payload.sucursalId = sucursalIds.length === 1 ? sucursalIds[0] : null;

  const rolId = Number(form.rolId);
  if (String(form.rolId).trim() !== '' && Number.isFinite(rolId)) {
    payload.rolId = rolId;
  }

  return payload;
};

const parseBoolish = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    String(value).toLowerCase() === 'true'
  );
};

/** Prioriza es_type_system del API; multi-sucursal implica cuenta empresa. */
const inferEsCuentaSistema = (row, sucursalIds, sucursalId) => {
  if (sucursalIds.length > 1) return false;

  const esTypeRaw = row.es_type_system ?? row.esTypeSystem ?? row.EsTypeSystem;
  if (esTypeRaw !== undefined && esTypeRaw !== null && String(esTypeRaw).trim() !== '') {
    return parseBoolish(esTypeRaw) ?? false;
  }

  const explicit = parseBoolish(
    row.esCuentaSistema ??
      row.EsCuentaSistema ??
      row.cuentaSistema ??
      row.CuentaSistema ??
      row.systemAccount
  );
  if (explicit !== undefined) return explicit;

  const hasSucursalId = sucursalId != null && String(sucursalId).trim() !== '';
  return hasSucursalId;
};

const mapUsuarioApiToForm = (raw) => {
  const row = raw && typeof raw === 'object' ? raw : {};
  const rolObj = row.Rol && typeof row.Rol === 'object' ? row.Rol : row.rol;
  const rid = row.rolId ?? row.RolID ?? row.rol_id ?? rolObj?.RolID ?? rolObj?.rolId;
  const sucursalId = row.sucursalId ?? row.SucursalID ?? row.sucursal_id;
  const sucursalIdsRaw =
    row.sucursalIds ??
    row.SucursalIDs ??
    row.sucursalesIds ??
    row.SucursalesIds ??
    row.sucursalesAsignadas ??
    row.SucursalesAsignadas ??
    row.sucursales;
  const sucursalIdsParsed = Array.isArray(sucursalIdsRaw)
    ? sucursalIdsRaw
        .map((s) =>
          s && typeof s === 'object'
            ? (s.sucursalId ?? s.SucursalID ?? s.id ?? s.Id)
            : s
        )
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
    : [];
  const sucursalIds =
    sucursalIdsParsed.length > 0
      ? Array.from(new Set(sucursalIdsParsed))
      : (sucursalId != null && String(sucursalId).trim() !== '' ? [String(sucursalId)] : []);
  const activoRaw = row.activo ?? row.Activo ?? row.activa ?? row.Activa;
  const activo =
    activoRaw === false ||
    activoRaw === 0 ||
    activoRaw === '0' ||
    String(activoRaw).toLowerCase() === 'false'
      ? false
      : true;
  const esCuentaSistema = inferEsCuentaSistema(row, sucursalIds, sucursalId);

  return {
    nombreUsuario: String(row.nombreUsuario ?? row.NombreUsuario ?? row.username ?? '').trim(),
    password: '',
    passwordConfirm: '',
    nombres: String(row.nombres ?? row.Nombres ?? '').trim(),
    apellidoPaterno: String(row.apellidoPaterno ?? row.ApellidoPaterno ?? '').trim(),
    apellidoMaterno: String(row.apellidoMaterno ?? row.ApellidoMaterno ?? '').trim(),
    sucursalIds,
    rolId: rid != null && String(rid).trim() !== '' ? String(rid) : '',
    esCuentaSistema,
    activo,
  };
};

function UsuarioCreateModal({
  open,
  onClose,
  onCreated,
  rolRows,
  rolesLoading,
  rolesSelectError,
  sucursales = [],
  sucursalesSelectError = null,
  mode = 'create',
  userToEdit = null,
}) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(initialUsuarioForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sucursalToAdd, setSucursalToAdd] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    if (isEdit && userToEdit?.id) {
      setForm({
        nombreUsuario: String(userToEdit.nombreUsuario || '').trim(),
        password: '',
        passwordConfirm: '',
        nombres: String(userToEdit.nombres || '').trim(),
        apellidoPaterno: String(userToEdit.apellidoPaterno || '').trim(),
        apellidoMaterno: String(userToEdit.apellidoMaterno || '').trim(),
        sucursalIds: Array.isArray(userToEdit.sucursalIds)
          ? userToEdit.sucursalIds.map((v) => String(v || '').trim()).filter(Boolean)
          : [],
        rolId: String(userToEdit.rolId || '').trim(),
        esCuentaSistema: Boolean(userToEdit.esCuentaSistema),
        activo: Boolean(userToEdit.activo),
      });
    } else {
      setForm(initialUsuarioForm());
    }
    setError(null);
    return undefined;
  }, [open, isEdit, userToEdit]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const availableSucursales = useMemo(() => {
    const selected = new Set((Array.isArray(form.sucursalIds) ? form.sucursalIds : []).map((v) => String(v)));
    return (Array.isArray(sucursales) ? sucursales : []).filter(
      (s) => !selected.has(String(s.sucursalId))
    );
  }, [form.sucursalIds, sucursales]);
  const canAddMoreSucursales = !form.esCuentaSistema || (form.sucursalIds || []).length < 1;

  const sucursalResumenLabels = useMemo(() => {
    const ids = Array.isArray(form.sucursalIds) ? form.sucursalIds : [];
    return ids.map((sid) => {
      const hit = sucursales.find((s) => String(s.sucursalId) === String(sid));
      return hit?.nombreSucursal || String(sid);
    });
  }, [form.sucursalIds, sucursales]);

  useEffect(() => {
    if (!open) return;
    if (!sucursalToAdd || !availableSucursales.some((s) => String(s.sucursalId) === String(sucursalToAdd))) {
      setSucursalToAdd(availableSucursales[0] ? String(availableSucursales[0].sucursalId) : '');
    }
  }, [open, availableSucursales, sucursalToAdd]);

  useEffect(() => {
    if (!form.esCuentaSistema) return;
    const current = Array.isArray(form.sucursalIds) ? form.sucursalIds : [];
    if (current.length <= 1) return;
    setForm((prev) => ({ ...prev, sucursalIds: [String(current[0])] }));
  }, [form.esCuentaSistema, form.sucursalIds]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const handleAddSucursal = () => {
    const sid = String(sucursalToAdd || '').trim();
    if (!sid) return;
    const current = Array.isArray(form.sucursalIds) ? form.sucursalIds.map((v) => String(v)) : [];
    if (current.includes(sid)) return;
    if (form.esCuentaSistema && current.length >= 1) return;
    setField('sucursalIds', [...current, sid]);
  };

  const handleRemoveSucursal = (sid) => {
    const current = Array.isArray(form.sucursalIds) ? form.sucursalIds.map((v) => String(v)) : [];
    setField(
      'sucursalIds',
      current.filter((v) => String(v) !== String(sid))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.nombreUsuario.trim() || !form.nombres.trim()) {
      setError('Completa nombre de usuario y nombre(s).');
      return;
    }

    if (!String(form.rolId).trim()) {
      setError('Selecciona un rol de usuario.');
      return;
    }

    if (!isEdit) {
      if (!form.password) {
        setError('Indica la contraseña.');
        return;
      }
      if (!form.passwordConfirm) {
        setError('Confirma la contraseña.');
        return;
      }
      if (form.password !== form.passwordConfirm) {
        setError('Las contraseñas no coinciden.');
        return;
      }
    } else if (form.password || form.passwordConfirm) {
      if (!form.passwordConfirm || form.password !== form.passwordConfirm) {
        setError('Las contraseñas no coinciden.');
        return;
      }
    }

    const sucursalIdsForm = Array.isArray(form.sucursalIds)
      ? form.sucursalIds.map((v) => String(v || '').trim()).filter(Boolean)
      : [];
    if (form.esCuentaSistema) {
      if (sucursalIdsForm.length === 0) {
        setError('Cuenta sistemas: indica una sucursal.');
        return;
      }
      if (sucursalIdsForm.length > 1) {
        setError('Cuenta sistemas: solo puedes asignar una sucursal.');
        return;
      }
    }

    const userId = String(userToEdit?.id || '').trim();
    if (isEdit && !userId) {
      setError('No se encontró el identificador del usuario para actualizar.');
      return;
    }

    const payload = isEdit ? buildUsuarioUpdatePayload(form) : buildUsuarioPayload(form);

    try {
      setSubmitting(true);
      const res = isEdit ? await api.Users.actualizar(userId, payload) : await api.Users.crear(payload);
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success !== false) {
        await onCreated?.();
        onClose();
        return;
      }

      const msg =
        data.message ||
        data.error ||
        data.mensaje ||
        (isEdit
          ? `No se pudo actualizar el usuario (${res.status}).`
          : `No se pudo crear el usuario (${res.status}).`);
      setError(String(msg));
    } catch (err) {
      setError(err?.message || (isEdit ? 'Error de red al actualizar el usuario.' : 'Error de red al crear el usuario.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="config-modal-backdrop"
      role="presentation"
      onClick={onClose}
      aria-hidden={!open}
    >
      <div
        className="config-modal config-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="config-usuario-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="config-modal-header">
          <h2 id="config-usuario-modal-title">{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</h2>
          <button type="button" className="config-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <form className="config-form config-form--modal" onSubmit={handleSubmit}>
          <div className="config-form-grid">
            <div
              className={`config-field config-field--span2 config-cuenta-access-panel${
                form.esCuentaSistema ? ' config-cuenta-access-panel--sistema' : ' config-cuenta-access-panel--empresa'
              }`}
              role="status"
            >
              <div className="config-cuenta-access-panel__top">
                <div className="config-cuenta-access-panel__icon" aria-hidden="true">
                  <span className="material-symbols-rounded">
                    {form.esCuentaSistema ? 'store' : 'domain'}
                  </span>
                </div>
                <div className="config-cuenta-access-panel__meta">
                  <h3 className="config-cuenta-access-panel__heading">Información de cuenta</h3>
                  <p className="config-cuenta-access-panel__subtitle">Tipo de acceso por sucursales</p>
                </div>
                <span className="config-cuenta-access-badge">
                  {form.esCuentaSistema ? 'Cuenta sistemas' : 'Cuenta empresa'}
                </span>
              </div>

              <p className="config-cuenta-access-panel__desc">
                {form.esCuentaSistema
                  ? 'Usuario sucursal — acceso limitado a una sola sucursal.'
                  : 'Usuario empresa — puede operar en varias sucursales a la vez.'}
              </p>

              <div className="config-cuenta-access-panel__sucursales">
                <div className="config-cuenta-access-panel__sucursales-head">
                  <span className="material-symbols-rounded" aria-hidden="true">
                    location_on
                  </span>
                  <span>Sucursales asignadas</span>
                  <span className="config-cuenta-access-count">{form.sucursalIds.length}</span>
                </div>
                {form.sucursalIds.length === 0 ? (
                  <p className="config-cuenta-access-empty">Aún no hay sucursales asignadas a esta cuenta.</p>
                ) : (
                  <ul className="config-cuenta-access-chips">
                    {form.sucursalIds.map((sid, index) => (
                      <li key={sid} className="config-cuenta-access-chip">
                        <span className="material-symbols-rounded" aria-hidden="true">
                          store
                        </span>
                        <span>{sucursalResumenLabels[index] || sid}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <label className="config-field config-field--span2">
              <span>Es cuenta sistemas</span>
              <select
                value={form.esCuentaSistema ? 'si' : 'no'}
                onChange={(e) => setField('esCuentaSistema', e.target.value === 'si')}
              >
                <option value="no">No — cuenta empresa (varias sucursales)</option>
                <option value="si">Sí — cuenta sistemas (una sucursal)</option>
              </select>
            </label>
            <label className="config-field config-field--span2">
              <span>Nombre de usuario *</span>
              <input
                type="text"
                autoComplete="username"
                value={form.nombreUsuario}
                onChange={(e) => setField('nombreUsuario', e.target.value)}
                placeholder="jperez"
                readOnly={isEdit}
                aria-readonly={isEdit}
              />
            </label>
            <div className="config-form-password-row">
              <label className="config-field">
                <span>{isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña *'}</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  placeholder={isEdit ? 'Dejar vacío para no cambiar' : 'TuPassword123'}
                />
              </label>
              <label className="config-field">
                <span>{isEdit ? 'Confirmar nueva contraseña' : 'Confirmar contraseña *'}</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={form.passwordConfirm}
                  onChange={(e) => setField('passwordConfirm', e.target.value)}
                  placeholder={isEdit ? 'Repite si cambias contraseña' : 'Repite la contraseña'}
                />
              </label>
            </div>
            <div className="config-form-password-row">
              <label className="config-field">
                <span>Sucursales</span>
                <div className="config-sucursal-add-row">
                  <select
                    value={sucursalToAdd}
                    onChange={(e) => setSucursalToAdd(e.target.value)}
                    disabled={!canAddMoreSucursales || availableSucursales.length === 0}
                  >
                    {!canAddMoreSucursales ? (
                      <option value="">Solo una sucursal para cuenta sistemas</option>
                    ) : availableSucursales.length === 0 ? (
                      <option value="">Sin sucursales disponibles</option>
                    ) : (
                      availableSucursales.map((s) => (
                        <option key={s.sucursalId} value={String(s.sucursalId)}>
                          {s.nombreSucursal}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    className="config-btn-secondary"
                    onClick={handleAddSucursal}
                    disabled={!canAddMoreSucursales || !sucursalToAdd}
                  >
                    Agregar
                  </button>
                </div>
                {form.esCuentaSistema ? (
                  <span className="config-field-hint">Cuenta sistemas: solo puedes agregar una sucursal.</span>
                ) : (
                  <span className="config-field-hint">Cuenta normal: puedes agregar varias sucursales.</span>
                )}
                {sucursalesSelectError ? (
                  <span className="config-field-hint config-field-hint--warn">{sucursalesSelectError}</span>
                ) : sucursales.length === 0 ? (
                  <span className="config-field-hint config-field-hint--warn">
                    No hay sucursales disponibles para esta empresa.
                  </span>
                ) : null}
              </label>
            </div>
            <div className="config-field config-field--span2">
              <span>Sucursales agregadas ({form.sucursalIds.length})</span>
              {form.sucursalIds.length > 0 ? (
                <div className="config-sucursal-selected-list">
                  {form.sucursalIds.map((sid) => {
                    const hit = sucursales.find((s) => String(s.sucursalId) === String(sid));
                    return (
                      <div key={sid} className="config-sucursal-selected-item">
                        <span>{hit?.nombreSucursal || sid}</span>
                        <button
                          type="button"
                          className="config-icon-btn"
                          aria-label="Quitar sucursal"
                          title="Quitar"
                          onClick={() => handleRemoveSucursal(sid)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="config-field-hint">Aún no agregas sucursales.</span>
              )}
            </div>
            <div className="config-rol-activo-row">
              <label className="config-field">
                <span>Rol de Usuario *</span>
                <select
                  value={form.rolId}
                  onChange={(e) => setField('rolId', e.target.value)}
                  disabled={rolesLoading}
                  required
                >
                  <option value="">{rolesLoading ? 'Cargando roles…' : 'Selecciona un rol'}</option>
                  {rolRows
                    .filter((r) => String(r.id || '').trim() !== '')
                    .map((r) => (
                      <option key={r.key} value={r.id}>
                        {r.nombre}
                      </option>
                    ))}
                </select>
                {rolesSelectError ? (
                  <span className="config-field-hint config-field-hint--warn">{rolesSelectError}</span>
                ) : null}
              </label>
              <label className="ios-toggle-row ios-toggle-row--inline">
                <span className="ios-toggle-row-label">Activo</span>
                <span className="ios-toggle">
                  <input
                    type="checkbox"
                    className="ios-toggle-input"
                    checked={form.activo}
                    onChange={(e) => setField('activo', e.target.checked)}
                    aria-label="Usuario activo"
                  />
                  <span className="ios-toggle-track" aria-hidden />
                </span>
              </label>
            </div>
            <div className="config-form-section-heading config-field--span2">
              <h3>Información de Persona</h3>
            </div>
            <label className="config-field config-field--span2">
              <span>Nombre(s) *</span>
              <input
                type="text"
                value={form.nombres}
                onChange={(e) => setField('nombres', e.target.value)}
                placeholder="Juan"
              />
            </label>
            <label className="config-field">
              <span>Apellido paterno</span>
              <input
                type="text"
                value={form.apellidoPaterno}
                onChange={(e) => setField('apellidoPaterno', e.target.value)}
                placeholder="Pérez"
              />
            </label>
            <label className="config-field">
              <span>Apellido materno</span>
              <input
                type="text"
                value={form.apellidoMaterno}
                onChange={(e) => setField('apellidoMaterno', e.target.value)}
                placeholder="García"
              />
            </label>
          </div>

          {error && <p className="config-feedback config-feedback--error">{error}</p>}

          <div className="config-form-actions config-form-actions--modal">
            <button type="button" className="config-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button type="submit" className="config-btn-primary" disabled={submitting}>
              {submitting ? (isEdit ? 'Guardando…' : 'Creando…') : (isEdit ? 'Guardar cambios' : 'Crear usuario')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsuarioDeleteConfirmModal({ open, user, submitting, error, onClose, onConfirm }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  if (!open || !user) return null;

  const label = String(user.nombreUsuario || '').trim() || String(user.id || '').trim() || '—';

  return (
    <div
      className="config-modal-backdrop"
      role="presentation"
      onClick={submitting ? undefined : onClose}
      aria-hidden={!open}
    >
      <div
        className="config-modal config-modal--confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="config-usuario-delete-title"
        aria-describedby="config-usuario-delete-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="config-modal-header">
          <h2 id="config-usuario-delete-title">Eliminar usuario</h2>
          <button
            type="button"
            className="config-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <div className="config-modal-confirm-body">
          <p id="config-usuario-delete-desc" className="config-modal-confirm-intro">
            Vas a eliminar de forma permanente la cuenta del siguiente usuario. Esta acción no se puede
            deshacer.
          </p>
          <div className="config-modal-confirm-user-card" aria-label="Datos del usuario">
            <div className="config-modal-confirm-user-line">
              <span className="config-modal-confirm-label">Usuario</span>
              <span className="config-modal-confirm-value">{label}</span>
            </div>
            {user.nombreCompleto && user.nombreCompleto !== '—' ? (
              <div className="config-modal-confirm-user-line">
                <span className="config-modal-confirm-label">Nombre</span>
                <span className="config-modal-confirm-value">{user.nombreCompleto}</span>
              </div>
            ) : null}
            {user.rolDisplay && user.rolDisplay !== '—' ? (
              <div className="config-modal-confirm-user-line">
                <span className="config-modal-confirm-label">Rol</span>
                <span className="config-modal-confirm-value">{user.rolDisplay}</span>
              </div>
            ) : null}
          </div>

          {error ? <p className="config-feedback config-feedback--error config-modal-confirm-error">{error}</p> : null}

          <div className="config-form-actions config-form-actions--modal config-modal-confirm-actions">
            <button type="button" className="config-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="button"
              className="config-btn-danger"
              onClick={onConfirm}
              disabled={submitting}
            >
              {submitting ? 'Eliminando…' : 'Eliminar usuario'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RolDeleteConfirmModal({ open, role, tipoDisplay, submitting, error, onClose, onConfirm }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  if (!open || !role) return null;

  const nombre = String(role.nombre || '').trim() || String(role.id || '').trim() || '—';

  return (
    <div
      className="config-modal-backdrop"
      role="presentation"
      onClick={submitting ? undefined : onClose}
      aria-hidden={!open}
    >
      <div
        className="config-modal config-modal--confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="config-rol-delete-title"
        aria-describedby="config-rol-delete-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="config-modal-header">
          <h2 id="config-rol-delete-title">Eliminar rol</h2>
          <button
            type="button"
            className="config-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <div className="config-modal-confirm-body">
          <p id="config-rol-delete-desc" className="config-modal-confirm-intro">
            Vas a eliminar este rol. Los usuarios que lo tengan asignado pueden verse afectados. Esta acción no
            se puede deshacer.
          </p>
          <div className="config-modal-confirm-user-card" aria-label="Datos del rol">
            <div className="config-modal-confirm-user-line">
              <span className="config-modal-confirm-label">Nombre</span>
              <span className="config-modal-confirm-value">{nombre}</span>
            </div>
            {tipoDisplay && tipoDisplay !== '—' ? (
              <div className="config-modal-confirm-user-line">
                <span className="config-modal-confirm-label">Tipo de cuenta</span>
                <span className="config-modal-confirm-value">{tipoDisplay}</span>
              </div>
            ) : null}
          </div>

          {error ? <p className="config-feedback config-feedback--error config-modal-confirm-error">{error}</p> : null}

          <div className="config-form-actions config-form-actions--modal config-modal-confirm-actions">
            <button type="button" className="config-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button
              type="button"
              className="config-btn-danger"
              onClick={onConfirm}
              disabled={submitting}
            >
              {submitting ? 'Eliminando…' : 'Eliminar rol'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsuariosPanel() {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteModalError, setDeleteModalError] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(true);
  const [usuariosError, setUsuariosError] = useState(null);
  const [rolRows, setRolRows] = useState([]);
  const [rolesSelectError, setRolesSelectError] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalesSelectError, setSucursalesSelectError] = useState(null);
  const [listTick, setListTick] = useState(0);
  const [busquedaUsuarios, setBusquedaUsuarios] = useState('');
  const [empresaIdUsuario, setEmpresaIdUsuario] = useState('');
  const [pageUsuarios, setPageUsuarios] = useState(1);
  const [usuariosPager, setUsuariosPager] = useState({
    page: 1,
    pageSize: SEARCH_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  /** Fuerza otra petición de búsqueda tras alta/edición/baja (p. ej. si la página sigue en 1 y solo cambia listTick). */
  const [usuariosRefetchKey, setUsuariosRefetchKey] = useState(0);

  const bumpList = useCallback(() => setListTick((n) => n + 1), []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setUserToEdit(null);
  }, []);
  const handleCreated = useCallback(async () => {
    setPageUsuarios(1);
    setUsuariosRefetchKey((k) => k + 1);
    bumpList();
  }, [bumpList]);

  const handleEditUser = useCallback(async (u) => {
    const userId = String(u?.id || '').trim();
    if (!userId) return;
    try {
      const res = await api.Users.getById(userId);
      const raw = await res.json().catch(() => ({}));
      const root = unwrapJsonRoot(raw);
      const fromApi = Array.isArray(root) ? root[0] : root;
      const mapped = mapUsuarioApiToForm(fromApi || {});
      const fallbackSucursalIds = Array.isArray(u?.sucursalIds)
        ? u.sucursalIds.map((v) => String(v || '').trim()).filter(Boolean)
        : [];
      const mappedSucursalIds = Array.isArray(mapped?.sucursalIds)
        ? mapped.sucursalIds.map((v) => String(v || '').trim()).filter(Boolean)
        : [];
      const mergedSucursalIds =
        mappedSucursalIds.length > 0 ? mappedSucursalIds : fallbackSucursalIds;
      setUserToEdit({
        id: userId,
        ...mapped,
        sucursalIds: mergedSucursalIds,
      });
      setModalOpen(true);
    } catch {
      setUserToEdit({
        id: userId,
        nombreUsuario: u.nombreUsuario || '',
        nombres: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        sucursalIds: Array.isArray(u?.sucursalIds) ? u.sucursalIds : [],
        rolId: '',
        esCuentaSistema: false,
        activo: true,
      });
      setModalOpen(true);
    }
  }, []);

  const openDeleteUserModal = useCallback((u) => {
    setDeleteModalError(null);
    setUserToDelete(u);
  }, []);

  const closeDeleteUserModal = useCallback(() => {
    if (deleteSubmitting) return;
    setUserToDelete(null);
    setDeleteModalError(null);
  }, [deleteSubmitting]);

  const confirmDeleteUser = useCallback(async () => {
    const u = userToDelete;
    const userId = String(u?.id || '').trim();
    if (!userId) return;
    setDeleteModalError(null);
    setDeleteSubmitting(true);
    try {
      const res = await api.Users.eliminar(userId);
      const data = await res.json().catch(() => ({}));
      const successFlag = data?.success;
      const okBody = successFlag === true || successFlag === 1 || successFlag === 'true';
      if (!res.ok || (successFlag !== undefined && !okBody)) {
        const msg =
          data?.message ||
          data?.error ||
          data?.mensaje ||
          `No se pudo eliminar el usuario (${res.status}).`;
        setDeleteModalError(String(msg));
        return;
      }
      setUserToDelete(null);
      await handleCreated();
    } catch (err) {
      setDeleteModalError(err?.message || 'Error de red al eliminar el usuario.');
    } finally {
      setDeleteSubmitting(false);
    }
  }, [userToDelete, handleCreated]);

  const cuentasActionClaves = useMemo(
    () => getActionClavesByModulo(user?.PermisosModel, ['cuentas', 'usuarios']),
    [user]
  );
  const canCreateUsuario = cuentasActionClaves.has('CREATE');
  const canUpdateUsuario = cuentasActionClaves.has('UPDATE');
  const canDeleteUsuario = cuentasActionClaves.has('DELETE');
  const showUsuarioActions = canUpdateUsuario || canDeleteUsuario;

  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      setUsuariosError(null);

      try {
        const empRes = await api.Users.empresaCuenta();

        let empresaId = '';
        if (!cancelled && empRes.ok) {
          const empRaw = await empRes.json().catch(() => ({}));
          const root = unwrapJsonRoot(empRaw);
          empresaId = pickEmpresaFromEmpresaCuenta(root).empresaId;
        }
        if (!cancelled && !empresaId) {
          empresaId = readEmpresaIdFromUser(user);
        }

        if (!cancelled) {
          if (!empresaId) {
            setEmpresaIdUsuario('');
            setUsuarios([]);
            setRolRows([]);
            setRolesSelectError(null);
            setSucursales([]);
            setSucursalesSelectError(null);
            setUsuariosError('No se identificó la empresa de la sesión; no se puede cargar el listado de usuarios.');
            setUsuariosLoading(false);
          } else {
            setEmpresaIdUsuario(String(empresaId).trim());
            const [rolesRes, sucRes] = await Promise.all([
              api.roles.getListadoOptionsByEmpresa(empresaId),
              api.getSucursales.byEmpresa(empresaId),
            ]);

            const rolesRaw = await rolesRes.json().catch(() => ({}));
            setRolRows(normalizeRolesList(rolesRaw));
            if (!rolesRes.ok) {
              setRolesSelectError(`No se pudieron cargar los roles (HTTP ${rolesRes.status}).`);
            } else {
              setRolesSelectError(null);
            }

            const sucRaw = await sucRes.json().catch(() => ({}));
            setSucursales(normalizeSucursalesList(sucRaw));
            if (!sucRes.ok) {
              setSucursalesSelectError(`No se pudieron cargar las sucursales (HTTP ${sucRes.status}).`);
            } else {
              setSucursalesSelectError(null);
            }
            setUsuariosError(null);
          }
        }
      } catch {
        if (!cancelled) {
          setEmpresaIdUsuario('');
          setUsuariosError('Error al cargar usuarios. Comprueba la sesión o la red.');
          setRolRows([]);
          setRolesSelectError(null);
          setSucursales([]);
          setSucursalesSelectError(null);
          setUsuariosLoading(false);
        }
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [user, listTick]);

  useEffect(() => {
    if (!empresaIdUsuario) {
      setUsuarios([]);
      setUsuariosPager({ page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 });
      setUsuariosLoading(false);
      return undefined;
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      setUsuariosLoading(true);
      try {
        const res = await api.Users.searchByEmpresa(empresaIdUsuario, {
          query: busquedaUsuarios.trim(),
          page: pageUsuarios,
        });
        const raw = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.ok) {
            const paged = unwrapSearchPagePayload(raw);
            setUsuariosPager({
              page: paged.page,
              pageSize: paged.pageSize,
              total: paged.total,
              totalPages: paged.totalPages,
            });
            setUsuarios(normalizeUsersList(paged.rows));
            setUsuariosError(null);
            if (paged.page !== pageUsuarios) {
              setPageUsuarios(paged.page);
            }
          } else {
            setUsuarios([]);
            setUsuariosPager({ page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 });
            setUsuariosError(`No se pudo buscar usuarios (HTTP ${res.status}).`);
          }
        }
      } catch {
        if (!cancelled) {
          setUsuarios([]);
          setUsuariosPager({ page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 });
          setUsuariosError('Error al buscar usuarios. Comprueba la sesión o la red.');
        }
      } finally {
        if (!cancelled) setUsuariosLoading(false);
      }
    }, 320);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [empresaIdUsuario, busquedaUsuarios, listTick, pageUsuarios, usuariosRefetchKey]);

  return (
    <article className="config-card config-card--roles">
      <div className="config-roles-toolbar">
        <h2>Usuarios</h2>
        <div className="config-toolbar-search">
          <label htmlFor="config-busqueda-usuarios" className="config-search-label">
            Buscar
          </label>
          <input
            id="config-busqueda-usuarios"
            type="search"
            className="config-search-input"
            placeholder="Buscar en servidor (nombre, usuario…)"
            value={busquedaUsuarios}
            onChange={(e) => {
              setBusquedaUsuarios(e.target.value);
              setPageUsuarios(1);
            }}
            autoComplete="off"
          />
        </div>
        {canCreateUsuario && (
          <button
            type="button"
            className="config-btn-primary"
            onClick={() => {
              setUserToEdit(null);
              setModalOpen(true);
            }}
          >
            Crear nuevo usuario
          </button>
        )}
      </div>

      {usuariosError && <p className="config-feedback config-feedback--error">{usuariosError}</p>}

      <div className="config-users-table" role="table" aria-label="Listado de usuarios">
        <div className="config-users-row config-users-row--head" role="row">
          <div role="columnheader">Usuario</div>
          <div role="columnheader">Nombre</div>
          <div role="columnheader">Rol</div>
          <div role="columnheader">Activo</div>
          {showUsuarioActions && <div role="columnheader">Acciones</div>}
        </div>
        {usuariosLoading ? (
          <div className="config-users-row config-users-row--empty" role="row">
            <div role="cell">Cargando usuarios…</div>
          </div>
        ) : usuarios.length === 0 ? (
          <div className="config-users-row config-users-row--empty" role="row">
            <div role="cell">
              {busquedaUsuarios.trim()
                ? 'No hay resultados para tu búsqueda.'
                : 'No hay usuarios para mostrar.'}
            </div>
          </div>
        ) : (
          usuarios.map((u) => (
            <div key={u.key} className="config-users-row" role="row">
              <div role="cell">{u.nombreUsuario}</div>
              <div role="cell">{u.nombreCompleto}</div>
              <div role="cell">{u.rolDisplay}</div>
              <div role="cell">{u.activoLabel}</div>
              {showUsuarioActions && (
                <div role="cell" className="config-row-actions">
                  {canUpdateUsuario && (
                    <button
                      type="button"
                      className="config-icon-btn"
                      aria-label="Editar usuario"
                      title="Editar"
                      onClick={() => handleEditUser(u)}
                    >
                      ✎
                    </button>
                  )}
                  {canDeleteUsuario && (
                    <button
                      type="button"
                      className="config-icon-btn config-icon-btn--danger"
                      aria-label="Eliminar usuario"
                      title="Eliminar"
                      onClick={() => openDeleteUserModal(u)}
                    >
                      🗑
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfigSearchPagination
        page={usuariosPager.page}
        pageSize={usuariosPager.pageSize}
        total={usuariosPager.total}
        totalPages={usuariosPager.totalPages}
        loading={usuariosLoading}
        onPageChange={setPageUsuarios}
      />

      <UsuarioCreateModal
        open={modalOpen}
        onClose={closeModal}
        mode={userToEdit ? 'edit' : 'create'}
        userToEdit={userToEdit}
        onCreated={handleCreated}
        rolRows={rolRows}
        rolesLoading={usuariosLoading}
        rolesSelectError={rolesSelectError}
        sucursales={sucursales}
        sucursalesSelectError={sucursalesSelectError}
      />

      <UsuarioDeleteConfirmModal
        open={Boolean(userToDelete)}
        user={userToDelete}
        submitting={deleteSubmitting}
        error={deleteModalError}
        onClose={closeDeleteUserModal}
        onConfirm={confirmDeleteUser}
      />
    </article>
  );
}

const initialRolForm = () => ({
  nombre: '',
});

const buildRolPayload = (form, permisosSeleccionados, empresaId = '') => {
  const payload = {
    nombre: form.nombre.trim().slice(0, 50),
    tipoCuenta: ALTA_TIPO_CUENTA,
    permisos: permisosSeleccionados,
  };
  const empresaIdRaw = String(empresaId ?? '').trim();
  if (empresaIdRaw) payload.empresaId = empresaIdRaw;
  return payload;
};

const parsePermisoKey = (key) => {
  const raw = String(key ?? '').trim();
  if (!raw) return null;
  const parts = raw.split('-').filter(Boolean);
  const prefix = parts[0] || '';
  const idRaw = parts.length > 1 ? parts[parts.length - 1] : raw;
  const idAsNumber = Number(idRaw);
  const id = Number.isFinite(idAsNumber) && String(idAsNumber) === String(idRaw) ? idAsNumber : idRaw;
  const tipoMap = { P: 'permiso', A: 'accion', L: 'legacy' };
  return {
    tipo: tipoMap[prefix] || 'permiso',
    id,
  };
};

const selectedPermisosToPayload = (permisosSel, permisosGroups) => {
  const selectedKeys = Object.entries(permisosSel || {})
    .filter(([, v]) => v)
    .map(([k]) => k);
  if (selectedKeys.length === 0) return [];

  const metaByKey = new Map();
  for (const group of permisosGroups || []) {
    for (const item of group.items || []) {
      metaByKey.set(item.key, { moduloId: group.id, modulo: group.legend });
    }
    for (const child of group.children || []) {
      for (const item of child.items || []) {
        metaByKey.set(item.key, { moduloId: child.id, modulo: child.legend });
      }
    }
  }

  return selectedKeys
    .map((key) => {
      const parsed = parsePermisoKey(key);
      if (!parsed) return null;
      const meta = metaByKey.get(key) || {};
      return {
        tipo: parsed.tipo,
        id: parsed.id,
        moduloId: meta.moduloId ?? null,
        modulo: meta.modulo ?? null,
      };
    })
    .filter(Boolean);
};

const tipoLabelFromOptions = (options, tipoCuenta) => {
  const list = Array.isArray(options) ? options : [];
  if (tipoCuenta == null || !Number.isFinite(tipoCuenta)) return '—';
  const hit = list.find((o) => o.value === tipoCuenta);
  return hit ? hit.label : String(tipoCuenta);
};

const asPermisosListadoArray = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.permisos)) return raw.permisos;
    if (Array.isArray(raw.elementos)) return raw.elementos;
    if (Array.isArray(raw.elementosNavegacion)) return raw.elementosNavegacion;
    if (Array.isArray(raw.items)) return raw.items;
  }
  return [];
};

const permisosCatalogToGroups = (raw) => {
  const rows = asPermisosListadoArray(raw);
  const isEnabled = (val) =>
    val === undefined ||
    val === null ||
    val === true ||
    val === 1 ||
    String(val).toLowerCase() === 'true';

  const map = new Map();
  const elementMeta = new Map();
  for (const row of rows) {
    if (row == null || typeof row !== 'object') continue;
    if (!isEnabled(row.Activo ?? row.activo)) continue;

    const elementIdVal = row.ElementoNavegacionID ?? row.elementoNavegacionId ?? row.id ?? row.Id;
    const elementId =
      elementIdVal != null && String(elementIdVal).trim() !== ''
        ? String(elementIdVal)
        : `tmp-${map.size + 1}`;
    const elementName =
      String(row.Nombre ?? row.nombre ?? row.NombreElemento ?? row.nombreElemento ?? '').trim() ||
      `Elemento ${elementId}`;
    const parentVal = row.PadreID ?? row.padreId ?? null;
    const parentId = parentVal != null && String(parentVal).trim() !== '' ? String(parentVal) : null;

    elementMeta.set(elementId, {
      id: elementId,
      title: elementName,
      parentId,
      orden: Number(row.Orden ?? row.orden ?? 0) || 0,
    });

    if (!map.has(elementId)) {
      map.set(elementId, {
        id: elementId,
        legend: elementName,
        parentId,
        orden: Number(row.Orden ?? row.orden ?? 0) || 0,
        items: new Map(),
      });
    }
    const group = map.get(elementId);
    const bucket = group.items;

    const permisos = Array.isArray(row.Permiso) ? row.Permiso : [];
    for (const permiso of permisos) {
      if (!permiso || typeof permiso !== 'object') continue;
      if (!isEnabled(permiso.Activo ?? permiso.activo)) continue;

      const keyVal =
        permiso.PermisoID ??
        permiso.permisoId ??
        permiso.id ??
        permiso.Id ??
        permiso.clave ??
        permiso.Clave;
      const key = keyVal != null && String(keyVal).trim() !== '' ? `P-${elementId}-${String(keyVal)}` : '';
      if (!key) continue;

      const permisoNombre =
        String(permiso.Nombre ?? permiso.nombre ?? permiso.label ?? permiso.Descripcion ?? '').trim() || key;
      bucket.set(key, { key, label: permisoNombre });
    }

    // Nueva estructura también incluye AccionElemento[] con Accion.
    const acciones = Array.isArray(row.AccionElemento) ? row.AccionElemento : [];
    for (const accionElem of acciones) {
      if (!accionElem || typeof accionElem !== 'object') continue;
      if (!isEnabled(accionElem.Activo ?? accionElem.activo)) continue;
      const action = accionElem.Accion && typeof accionElem.Accion === 'object' ? accionElem.Accion : null;
      const keyVal =
        action?.AccionID ??
        action?.accionId ??
        accionElem.AccionID ??
        accionElem.accionId ??
        action?.Clave ??
        action?.clave;
      const key = keyVal != null && String(keyVal).trim() !== '' ? `A-${elementId}-${String(keyVal)}` : '';
      if (!key) continue;
      const actionName =
        String(action?.Nombre ?? action?.nombre ?? action?.Clave ?? action?.clave ?? '').trim() || key;
      bucket.set(key, { key, label: actionName });
    }

    // Compatibilidad con estructura antigua plana.
    const legacyKeyVal =
      row.PermisoID ??
      row.permisoId ??
      row.ElementoNavegacionID ??
      row.elementoNavegacionId ??
      row.id ??
      row.clave ??
      row.Clave ??
      row.codigo ??
      row.key;
    if (permisos.length === 0 && legacyKeyVal != null && String(legacyKeyVal).trim() !== '') {
      const key = `L-${elementId}-${String(legacyKeyVal)}`;
      const label =
        String(
          row.Nombre ??
            row.nombre ??
            row.descripcion ??
            row.Descripcion ??
            row.label ??
            row.NombreElemento ??
            row.nombreElemento ??
            key
        ).trim() || key;
      bucket.set(key, { key, label });
    }
  }
  const childrenByParent = new Map();
  for (const meta of elementMeta.values()) {
    if (!meta.parentId) continue;
    if (!childrenByParent.has(meta.parentId)) childrenByParent.set(meta.parentId, []);
    childrenByParent.get(meta.parentId).push(meta);
  }

  const allGroups = Array.from(map.values());
  const groupById = new Map(allGroups.map((g) => [g.id, g]));
  const withChildren = allGroups.map((group) => {
    const childMetas = (childrenByParent.get(group.id) || [])
      .sort((a, b) => (a.orden !== b.orden ? a.orden - b.orden : a.title.localeCompare(b.title)));
    const children = childMetas
      .map((meta) => groupById.get(meta.id))
      .filter(Boolean)
      .map((child) => ({
        id: child.id,
        legend: child.legend,
        items: Array.from(child.items.values()),
      }))
      .filter((child) => child.items.length > 0);

    return {
      id: group.id,
      legend: group.legend,
      parentId: group.parentId ?? null,
      orden: group.orden ?? 0,
      items: Array.from(group.items.values()),
      children,
    };
  });

  return withChildren
    .sort((a, b) => (a.orden !== b.orden ? a.orden - b.orden : a.legend.localeCompare(b.legend)))
    .filter((group) => {
      const isTopLevel = group.parentId == null;
      const hasContent = group.items.length > 0 || (group.children || []).length > 0;
      return isTopLevel && hasContent;
    });
};

function RolCreateModal(props) {
  const {
    open,
    onClose,
    permisosGroups = [],
    permisosCatalogLoading = false,
    permisosCatalogError = null,
    empresaId = '',
    mode = 'create',
    roleToEdit = null,
    onCreated,
  } = props || {};
  const isEdit = mode === 'edit';

  const [form, setForm] = useState(initialRolForm);
  const [permisosSel, setPermisosSel] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setForm(
      isEdit
        ? {
            nombre: String(roleToEdit?.nombre || '').trim(),
          }
        : initialRolForm()
    );
    setError(null);
    const permisoIdsSeleccionados = new Set(
      (Array.isArray(roleToEdit?.permisoIds) ? roleToEdit.permisoIds : [])
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
    );
    const init = {};
    for (const g of permisosGroups) {
      for (const it of g.items || []) {
        if (!isVisiblePermisoLabel(it?.label)) continue;
        const itemId = String(it?.key ?? '').split('-').pop();
        init[it.key] = isEdit && permisoIdsSeleccionados.has(String(itemId));
      }
      for (const child of g.children || []) {
        for (const it of child.items || []) {
          if (!isVisiblePermisoLabel(it?.label)) continue;
          const itemId = String(it?.key ?? '').split('-').pop();
          init[it.key] = isEdit && permisoIdsSeleccionados.has(String(itemId));
        }
      }
    }
    setPermisosSel(init);
    return undefined;
  }, [open, permisosGroups, isEdit, roleToEdit]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.nombre.trim()) {
      setError('Indica el nombre del rol.');
      return;
    }

    const permisosSeleccionados = selectedPermisosToPayload(permisosSel, permisosGroups);
    const payload = buildRolPayload(form, permisosSeleccionados, empresaId);

    try {
      setSubmitting(true);
      if (isEdit) {
        const rolId = String(roleToEdit?.id || '').trim();
        if (!rolId) {
          setError('No se encontró el identificador del rol para actualizar.');
          return;
        }
      }
      const res = isEdit
        ? await api.roles.actualizar(String(roleToEdit?.id || ''), payload)
        : await api.roles.crear(payload);
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success !== false) {
        onCreated();
        onClose();
        return;
      }

      const msg =
        data.message ||
        data.error ||
        data.mensaje ||
        (isEdit
          ? `No se pudo actualizar el rol (${res.status}).`
          : `No se pudo crear el rol (${res.status}).`);
      setError(String(msg));
    } catch (err) {
      setError(err?.message || (isEdit ? 'Error de red al actualizar el rol.' : 'Error de red al crear el rol.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="config-modal-backdrop"
      role="presentation"
      onClick={onClose}
      aria-hidden={!open}
    >
      <div
        className="config-modal config-modal--wide config-modal--rol"
        role="dialog"
        aria-modal="true"
        aria-labelledby="config-rol-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="config-modal-header">
          <h2 id="config-rol-modal-title">{isEdit ? 'Editar rol' : 'Nuevo rol'}</h2>
          <button type="button" className="config-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <form className="config-form config-form--modal" onSubmit={handleSubmit}>
          <div className="config-form-grid">
            <label className="config-field config-field--span2">
              <span>Nombre *</span>
              <input
                type="text"
                maxLength={50}
                value={form.nombre}
                onChange={(e) => setField('nombre', e.target.value)}
                placeholder="Ej. Administrador sucursal"
              />
            </label>
          </div>

          <div className="config-permisos-scroll">
            {permisosCatalogLoading && permisosGroups.length === 0 ? (
              <p className="config-permisos-hint">Cargando permisos…</p>
            ) : permisosCatalogError ? (
              <p className="config-feedback config-feedback--error">{permisosCatalogError}</p>
            ) : !permisosGroups.some((g) => (g.items || []).length) ? (
              <p className="config-permisos-hint">No hay permisos en el catálogo.</p>
            ) : (
              permisosGroups.map((group) => {
                const children = Array.isArray(group.children) ? group.children : [];
                const groupSelectableKeys = [];
                for (const child of children) {
                  const childItemsRaw = dedupePermisosByLabel(dedupePermisosByKey(child.items || []));
                  const accessItem = childItemsRaw.find((item) => isAccessPermisoLabel(item?.label)) || null;
                  const childItems = childItemsRaw.filter(
                    (item) => isVisiblePermisoLabel(item?.label) && !isAccessPermisoLabel(item?.label)
                  );
                  if (accessItem) groupSelectableKeys.push(accessItem.key);
                  for (const item of childItems) {
                    groupSelectableKeys.push(item.key);
                  }
                }
                const canToggleGroup = groupSelectableKeys.length > 0;
                const isGroupChecked =
                  canToggleGroup && groupSelectableKeys.every((key) => Boolean(permisosSel[key]));
                const syncGroupPermisos = (checked) => {
                  if (!canToggleGroup) return;
                  setPermisosSel((prev) => {
                    const next = { ...prev };
                    for (const key of groupSelectableKeys) {
                      next[key] = checked;
                    }
                    return next;
                  });
                  setError(null);
                };

                return (
                  <details key={group.legend} className="config-permisos-dropdown-group">
                    <summary className="config-permisos-dropdown-summary">
                      {canToggleGroup && (
                        <label className="config-permisos-access-toggle" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="config-permisos-checkbox"
                            checked={isGroupChecked}
                            onChange={(e) => syncGroupPermisos(e.target.checked)}
                          />
                        </label>
                      )}
                      <span>{group.legend}</span>
                    </summary>
                    {children.map((child) => {
                      const childItemsRaw = dedupePermisosByLabel(dedupePermisosByKey(child.items || []));
                      const accessItem = childItemsRaw.find((item) => isAccessPermisoLabel(item?.label)) || null;
                      const childItems = childItemsRaw.filter(
                        (item) => isVisiblePermisoLabel(item?.label) && !isAccessPermisoLabel(item?.label)
                      );
                      const childTotalCount = childItems.length + (accessItem ? 1 : 0);
                      if (childTotalCount === 0) return null;

                      const syncModulePermisos = (checked) => {
                        setPermisosSel((prev) => {
                          const next = { ...prev };
                          if (accessItem) next[accessItem.key] = checked;
                          for (const item of childItems) {
                            next[item.key] = checked;
                          }
                          return next;
                        });
                        setError(null);
                      };

                      if (childItems.length === 0) {
                        return (
                          <div key={`${group.legend}-${child.id}`} className="config-permisos-subrow-static">
                            {accessItem && (
                              <label className="config-permisos-access-toggle">
                                <input
                                  type="checkbox"
                                  className="config-permisos-checkbox"
                                  checked={Boolean(permisosSel[accessItem.key])}
                                  onChange={(e) => {
                                    syncModulePermisos(e.target.checked);
                                  }}
                                />
                              </label>
                            )}
                            <span>{child.legend}</span>
                          </div>
                        );
                      }
                      return (
                        <details key={`${group.legend}-${child.id}`} className="config-permisos-subdropdown">
                          <summary className="config-permisos-subsummary">
                            {accessItem && (
                              <label
                                className="config-permisos-access-toggle"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="config-permisos-checkbox"
                                  checked={Boolean(permisosSel[accessItem.key])}
                                  onChange={(e) => {
                                    syncModulePermisos(e.target.checked);
                                  }}
                                />
                              </label>
                            )}
                            <span>{child.legend}</span>
                          </summary>
                          <div className="config-permisos-checklist">
                            {childItems.map((item) => (
                              <label key={`${child.id}-${item.key}`} className="config-permisos-check-row">
                                <input
                                  type="checkbox"
                                  className="config-permisos-checkbox"
                                  checked={Boolean(permisosSel[item.key])}
                                  onChange={(e) => {
                                    setPermisosSel((prev) => ({ ...prev, [item.key]: e.target.checked }));
                                    setError(null);
                                  }}
                                />
                                <span>{item.label}</span>
                              </label>
                            ))}
                          </div>
                        </details>
                      );
                    })}
                  </details>
                );
              })
            )}
          </div>

          {error && <p className="config-feedback config-feedback--error">{error}</p>}

          <div className="config-form-actions config-form-actions--modal">
            <button type="button" className="config-btn-secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </button>
            <button type="submit" className="config-btn-primary" disabled={submitting}>
              {submitting ? (isEdit ? 'Guardando…' : 'Creando…') : (isEdit ? 'Guardar cambios' : 'Crear rol')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RolesPanel() {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleteRoleSubmitting, setDeleteRoleSubmitting] = useState(false);
  const [deleteRoleModalError, setDeleteRoleModalError] = useState(null);
  const [empresaId, setEmpresaId] = useState('');
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);
  const [tiposCuentaOptions, setTiposCuentaOptions] = useState([]);
  const [tiposError, setTiposError] = useState(null);
  const [permisosCatalogRaw, setPermisosCatalogRaw] = useState(null);
  const [permisosCatalogError, setPermisosCatalogError] = useState(null);
  const [permisosListLoading, setPermisosListLoading] = useState(true);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [busquedaRoles, setBusquedaRoles] = useState('');
  const [pageRoles, setPageRoles] = useState(1);
  const [rolesPager, setRolesPager] = useState({
    page: 1,
    pageSize: SEARCH_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  /** Fuerza otra petición de búsqueda tras alta/edición/baja de rol. */
  const [rolesRefetchKey, setRolesRefetchKey] = useState(0);

  const permisosGroups = useMemo(
    () => permisosCatalogToGroups(permisosCatalogRaw ?? []),
    [permisosCatalogRaw]
  );

  const rolesActionClaves = useMemo(
    () => getActionClavesByModulo(user?.PermisosModel, ['roles']),
    [user]
  );
  const canCreateRol = rolesActionClaves.has('CREATE');
  const canUpdateRol = rolesActionClaves.has('UPDATE');
  const canDeleteRol = rolesActionClaves.has('DELETE');
  const showRolActions = canUpdateRol || canDeleteRol;

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setRoleToEdit(null);
  }, []);

  const handleCreated = useCallback(async () => {
    setPageRoles(1);
    setRolesRefetchKey((k) => k + 1);
  }, []);

  const handleEditRole = useCallback(async (roleRow) => {
    const roleId = String(roleRow?.id || '').trim();
    if (!roleId) return;
    try {
      const res = await api.roles.obtener(roleId);
      const raw = await res.json().catch(() => ({}));
      const root = unwrapJsonRoot(raw);
      const fromApi = Array.isArray(root) ? root[0] : root;
      const nombre = String(fromApi?.nombre ?? fromApi?.Nombre ?? roleRow?.nombre ?? '').trim();
      const permisoIdsRaw = Array.isArray(fromApi?.permisoIds)
        ? fromApi.permisoIds
        : Array.isArray(fromApi?.PermisoIds)
          ? fromApi.PermisoIds
          : Array.isArray(fromApi?.Permiso)
            ? fromApi.Permiso.map((p) => p?.permisoId ?? p?.PermisoID ?? p?.id ?? p?.Id)
            : [];
      const permisoIds = permisoIdsRaw
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
      setRoleToEdit({
        ...roleRow,
        nombre: nombre || roleRow?.nombre || '—',
        permisoIds,
      });
      setModalOpen(true);
    } catch {
      setRoleToEdit(roleRow);
      setModalOpen(true);
    }
  }, []);

  const openDeleteRoleModal = useCallback((roleRow) => {
    setDeleteRoleModalError(null);
    setRoleToDelete(roleRow);
  }, []);

  const closeDeleteRoleModal = useCallback(() => {
    if (deleteRoleSubmitting) return;
    setRoleToDelete(null);
    setDeleteRoleModalError(null);
  }, [deleteRoleSubmitting]);

  const confirmDeleteRole = useCallback(async () => {
    const row = roleToDelete;
    const roleId = String(row?.id || '').trim();
    if (!roleId) return;
    setDeleteRoleModalError(null);
    setDeleteRoleSubmitting(true);
    try {
      const res = await api.roles.eliminar(roleId);
      const data = await res.json().catch(() => ({}));
      const successFlag = data?.success;
      const okBody = successFlag === true || successFlag === 1 || successFlag === 'true';
      if (!res.ok || (successFlag !== undefined && !okBody)) {
        const msg =
          data?.message ||
          data?.error ||
          data?.mensaje ||
          `No se pudo eliminar el rol (${res.status}).`;
        setDeleteRoleModalError(String(msg));
        return;
      }
      setRoleToDelete(null);
      await handleCreated();
    } catch (err) {
      setDeleteRoleModalError(err?.message || 'Error de red al eliminar el rol.');
    } finally {
      setDeleteRoleSubmitting(false);
    }
  }, [roleToDelete, handleCreated]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setRolesLoading(true);
      setRolesError(null);
      setTiposError(null);
      setPermisosListLoading(true);
      setPermisosCatalogError(null);

      try {
        const [empRes, tiposRes] = await Promise.all([
          api.Users.empresaCuenta(),
          api.tiposCuentas.listar(),
        ]);

        let empresaId = '';
        if (!cancelled && empRes.ok) {
          const empRaw = await empRes.json().catch(() => ({}));
          const root = unwrapJsonRoot(empRaw);
          empresaId = pickEmpresaFromEmpresaCuenta(root).empresaId;
        }
        if (!cancelled && !empresaId) {
          empresaId = readEmpresaIdFromUser(user);
        }
        if (!cancelled) setEmpresaId(String(empresaId || '').trim());

        if (!cancelled) {
          const raw = await tiposRes.json().catch(() => ({}));
          const opts = normalizeTiposCuentaOptions(raw);
          setTiposCuentaOptions(opts);
          if (!opts.length) {
            setTiposError(
              tiposRes.ok
                ? 'No se encontraron tipos de cuenta en la respuesta. Revisa el formato del API.'
                : `No se pudieron cargar los tipos de cuenta (HTTP ${tiposRes.status}). ¿Sesión iniciada?`
            );
          } else {
            setTiposError(null);
          }
        }

        if (!cancelled) {
          if (!empresaId) {
            setRoles([]);
            setRolesError('No se identificó la empresa de la sesión; no se puede cargar el listado de roles.');
            setPermisosCatalogRaw([]);
            setPermisosCatalogError('No se pudo cargar el catálogo de elementos: empresa inválida en sesión.');
            setRolesLoading(false);
          } else {
            const elementosRes = await api.elementosNavegacion.byEmpresa(empresaId);
            const elementosRaw = await elementosRes.json().catch(() => ({}));
            setPermisosCatalogRaw(elementosRaw);
            if (!elementosRes.ok) {
              setPermisosCatalogError(
                `No se pudo cargar el catálogo de elementos (HTTP ${elementosRes.status}).`
              );
            } else {
              setPermisosCatalogError(null);
            }
            setRolesError(null);
          }
        }
      } catch {
        if (!cancelled) {
          setRolesError('Error al cargar roles. Comprueba la sesión o la red.');
          setTiposError('Error al cargar datos. Comprueba la sesión o la red.');
          setPermisosCatalogRaw(null);
          setPermisosCatalogError('Error al cargar el catálogo de permisos.');
          setRolesLoading(false);
        }
      } finally {
        if (!cancelled) {
          setPermisosListLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!empresaId) {
      setRoles([]);
      setRolesPager({ page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 });
      setRolesLoading(false);
      return undefined;
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      setRolesLoading(true);
      try {
        const res = await api.roles.searchByEmpresa(empresaId, {
          query: busquedaRoles.trim(),
          page: pageRoles,
        });
        const raw = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.ok) {
            const paged = unwrapSearchPagePayload(raw);
            setRolesPager({
              page: paged.page,
              pageSize: paged.pageSize,
              total: paged.total,
              totalPages: paged.totalPages,
            });
            setRoles(normalizeRolesList(paged.rows));
            setRolesError(null);
            if (paged.page !== pageRoles) {
              setPageRoles(paged.page);
            }
          } else {
            setRoles([]);
            setRolesPager({ page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 });
            setRolesError(`No se pudo buscar roles (HTTP ${res.status}).`);
          }
        }
      } catch {
        if (!cancelled) {
          setRoles([]);
          setRolesPager({ page: 1, pageSize: SEARCH_PAGE_SIZE, total: 0, totalPages: 0 });
          setRolesError('Error al buscar roles. Comprueba la sesión o la red.');
        }
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    }, 320);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [empresaId, busquedaRoles, pageRoles, rolesRefetchKey]);

  return (
    <article className="config-card config-card--roles">
      <div className="config-roles-toolbar">
        <h2>Roles</h2>
        <div className="config-toolbar-search">
          <label htmlFor="config-busqueda-roles" className="config-search-label">
            Buscar
          </label>
          <input
            id="config-busqueda-roles"
            type="search"
            className="config-search-input"
            placeholder="Buscar en servidor (nombre del rol…)"
            value={busquedaRoles}
            onChange={(e) => {
              setBusquedaRoles(e.target.value);
              setPageRoles(1);
            }}
            autoComplete="off"
          />
        </div>
        {canCreateRol && (
          <button
            type="button"
            className="config-btn-primary"
            onClick={() => {
              setRoleToEdit(null);
              setModalOpen(true);
            }}
          >
            Crear nuevo rol
          </button>
        )}
      </div>

      {rolesError && <p className="config-feedback config-feedback--error">{rolesError}</p>}
      {tiposError && <p className="config-feedback config-feedback--error">{tiposError}</p>}

      <div className="config-roles-table" role="table" aria-label="Listado de roles">
        <div className="config-roles-row config-roles-row--head" role="row">
          <div role="columnheader" className="config-roles-col-name">Nombre</div>
          {showRolActions && <div role="columnheader" className="config-roles-col-actions">Acciones</div>}
          <div role="columnheader" className="config-roles-col-tipo">Tipo de cuenta</div>
        </div>
        {rolesLoading ? (
          <div className="config-roles-row config-roles-row--empty" role="row">
            <div role="cell">Cargando roles…</div>
          </div>
        ) : roles.length === 0 ? (
          <div className="config-roles-row config-roles-row--empty" role="row">
            <div role="cell">
              {busquedaRoles.trim()
                ? 'No hay resultados para tu búsqueda.'
                : 'No hay roles para mostrar.'}
            </div>
          </div>
        ) : (
          roles.map((r) => (
            <div key={r.key} className="config-roles-row" role="row">
              <div role="cell" className="config-roles-col-name">{r.nombre}</div>
              {showRolActions && (
                <div role="cell" className="config-row-actions config-roles-col-actions">
                  {canUpdateRol && (
                    <button
                      type="button"
                      className="config-icon-btn"
                      aria-label="Editar rol"
                      title="Editar"
                      onClick={() => handleEditRole(r)}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">
                        edit
                      </span>
                    </button>
                  )}
                  {canDeleteRol && (
                    <button
                      type="button"
                      className="config-icon-btn config-icon-btn--danger"
                      aria-label="Eliminar rol"
                      title="Eliminar"
                      onClick={() => openDeleteRoleModal(r)}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">
                        delete
                      </span>
                    </button>
                  )}
                </div>
              )}
              <div role="cell" className="config-roles-col-tipo">
                {tipoLabelFromOptions(tiposCuentaOptions ?? [], r.tipoCuenta)}
              </div>
            </div>
          ))
        )}
      </div>

      <ConfigSearchPagination
        page={rolesPager.page}
        pageSize={rolesPager.pageSize}
        total={rolesPager.total}
        totalPages={rolesPager.totalPages}
        loading={rolesLoading}
        onPageChange={setPageRoles}
      />

      <RolCreateModal
        open={modalOpen}
        onClose={closeModal}
        mode={roleToEdit ? 'edit' : 'create'}
        roleToEdit={roleToEdit}
        empresaId={empresaId}
        permisosGroups={permisosGroups}
        permisosCatalogLoading={permisosListLoading}
        permisosCatalogError={permisosCatalogError}
        onCreated={handleCreated}
      />

      <RolDeleteConfirmModal
        open={Boolean(roleToDelete)}
        role={roleToDelete}
        tipoDisplay={
          roleToDelete
            ? tipoLabelFromOptions(tiposCuentaOptions ?? [], roleToDelete.tipoCuenta)
            : '—'
        }
        submitting={deleteRoleSubmitting}
        error={deleteRoleModalError}
        onClose={closeDeleteRoleModal}
        onConfirm={confirmDeleteRole}
      />
    </article>
  );
}

export default function Configuraciones() {
  const { seccion } = useParams();
  const normalized = String(seccion || '').trim().toLowerCase();
  const isRoles = normalized === 'roles';
  const isUsuarios = normalized === 'usuarios';

  if (!isRoles && !isUsuarios) {
    return <Navigate to="/seguridad/usuarios" replace />;
  }

  return (
    <section className="config-page">
      <header className="config-page-header">
        <h1>Seguridad</h1>
        <p>Usuarios y roles.</p>
      </header>

      {!isRoles ? (
        <UsuariosPanel />
      ) : (
        <RolesPanel />
      )}
    </section>
  );
}
